import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  EmailValidationRules,
  FeatureFlags,
  StorageKeys,
} from '@core/constants';
import { HelpersService } from '@core/helpers/helpers.service';
import { AlertService } from '@core/services/alert/alert.service';
import { InputTextModule } from 'primeng/inputtext';
import { CustomerService } from '@core/services/customer/customer.service';
import {
  ButtonModule,
  FormValidationMessageModule,
  formatPhone,
  PhoneComponent,
} from '@shipmercury/mercury-ui';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { PublicBaseComponent } from 'src/app/public-base.component';

@Component({
  selector: 'au-request-account',
  templateUrl: './request-account.component.html',
  styleUrls: ['./request-account.component.scss'],
  imports: [
    NgTemplateOutlet,
    CommonModule,
    ButtonModule,
    FormValidationMessageModule,
    ReactiveFormsModule,
    InputTextModule,
    PhoneComponent,
    RecaptchaModule,
  ],
})
export class RequestAccountComponent
  extends PublicBaseComponent
  implements OnInit
{
  @ViewChild('phone') phoneComponent!: PhoneComponent;
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;

  form!: FormGroup;
  isPhoneNumberValid: boolean = false;
  requestSuccessful: boolean = false;
  enableRecaptcha: boolean = true;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private helpersService: HelpersService,
    private customerService: CustomerService,
    private alertService: AlertService
  ) {
    super();

    if (this.featureFlagService.unleashClient?.toggles.length > 0) {
      this.initFeatureFlags();
    }
    if (this.disableByAutomationUserAgent()) {
      this.enableRecaptcha = false;
    }
  }

  initFeatureFlags() {
    this.enableRecaptcha = this.featureFlagService.featureFlagEnabled(
      FeatureFlags.ENABLE_RECAPTCHA_LOGIN
    );
  }

  ngOnInit() {
    this.storageService.delete(StorageKeys.authUser);
    this.storageService.delete(StorageKeys.captchaResponse);

    this.createForm();
  }

  createForm() {
    this.form = this.formBuilder.group({
      companyName: ['', [Validators.required]],
      companyWebsite: ['', [Validators.required]],
      contactName: ['', [Validators.required]],
      contactPhone: this.formBuilder.group({
        phoneCountryCode: ['+1'],
        phoneNumber: ['', [Validators.required]],
        phoneNumberExtension: [null],
      }),
      contactEmail: ['', EmailValidationRules],
    });
  }

  submitRequestWithCaptcha(): void {
    this.captchaRef.execute();
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);
      this.requestAccount();
      this.captchaRef.reset();
    }
  }

  requestAccount() {
    this.helpersService.markAsDirty(this.form);

    // TODO: Change after changing phone component
    // this is a workaround for triggering phone validation
    // when the phone control is empty.
    const phoneControl = this.form.get('contactPhone')?.get('phoneNumber');
    if (!phoneControl || !phoneControl.value) {
      phoneControl?.setValue('1');
      this.phoneComponent.onFocusOutHandler();
      phoneControl?.setValue('');
    }

    if (this.form.valid && this.isPhoneNumberValid) {
      const reqBody = this.getRequestBody();
      this.customerService.requestAccount(reqBody).subscribe((res) => {
        if (res && res.success) {
          this.alertService.success({
            title: 'Success',
            message: 'We have received your account request.',
          });

          this.requestSuccessful = true;
        }
      });
    }
  }

  getRequestBody() {
    const lastName = this.form
      .get('contactName')
      ?.value.split(' ')
      .slice(-1)
      .join(' ');
    const firstName =
      this.form.get('contactName')?.value.split(' ').slice(0, -1).join(' ') ||
      lastName;

    const requestBody = {
      name: this.form.get('companyName')?.value,
      website: 'https://' + this.form.get('companyWebsite')?.value,
      notSendInboundEmails: true,

      customerUserRequestDTO: {
        type: 'ADMIN',
        firstName: firstName,
        lastName: lastName,
        email: this.form.get('contactEmail')?.value,
        phone: formatPhone(this.form.get('contactPhone')?.value),
      },
    };

    return requestBody;
  }

  navigateToLogin() {
    this.router.navigateByUrl('login');
  }

  navigateToShipLite() {
    this.router.navigateByUrl('ship-lite');
  }

  navigateToShipMercury() {
    window.open('https://www.shipmercury.com', '_self');
  }
}
